import { useState } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { Button, FormError, LoadFailure, Skeleton } from '../../../core/index.js'
import { useCensus, useRecordCensus } from '../../hooks.js'
import { addDays, rowFor } from '../censusCardData.js'
import type { TodayFilter } from './todayData.js'

const INPUT = 'w-20 rounded border border-marine/30 bg-white px-2 py-1 text-right text-sm font-bold text-ink focus:outline-none focus:border-marine'

/**
 * Today's census on the Today tab (facility operations dashboard plan §3): one count per unit beside yesterday's and
 * the forecast, and one Record for every unit changed (the week ahead is `WeekAhead`, beside it). Recording is the trigger: the engine
 * answers at once, and what it did, or left to a person, shows under Needs you and in the engine's log.
 */
export function TodayCensus({ facilityId, today, filter, anchorId }: { facilityId: string; today: string; filter: TodayFilter; anchorId: string }) {
  const census = useCensus(facilityId, { from: addDays(today, -1), to: today })
  const record = useRecordCensus(facilityId)
  const [draft, setDraft] = useState<Record<string, string>>({})
  if (census.isError) {
    return <LoadFailure size="xs" message="Could not load the census." onRetry={() => void census.refetch()} retrying={census.isFetching} />
  }
  const view = census.data
  if (!view) {
    return <Skeleton label="Loading the census" />
  }
  const units = view.units.filter((unit) => filter.unitId === null || unit.id === filter.unitId)
  if (units.length === 0) {
    return <p className="text-xs text-ink-mid">No units are kept, so there is no census to count. Add them under Configuration › Details.</p>
  }
  const changed = units.flatMap((unit) => {
    const text = draft[unit.id]
    const value = Number(text)
    return text !== undefined && text.trim() !== '' && Number.isInteger(value) && value >= 0 ? [{ unitId: unit.id, date: today, census: value }] : []
  })
  return (
    <div id={anchorId} className="flex flex-col gap-3 scroll-mt-4">
      <div className="flex flex-col divide-y divide-marine/10">
        {units.map((unit) => {
          const row = rowFor(view, today, unit.id)
          const yesterday = rowFor(view, addDays(today, -1), unit.id)
          const isRecorded = row !== null && row.census !== null && row.basis === 'recorded'
          return (
            <label key={unit.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-bold text-ink">{unit.name}</span>
                <span className="block text-[10px] text-ink-mid">
                  {unit.beds !== null ? `${unit.beds} beds` : 'beds not kept'}
                  {yesterday?.census !== null && yesterday?.census !== undefined ? ` · yesterday ${yesterday.census}` : ''}
                </span>
              </span>
              <span className={`text-lg font-black ${isRecorded ? 'text-ink' : 'italic text-ink-mid'}`}>{row?.census ?? '—'}</span>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${isRecorded ? 'bg-green-lt text-green' : 'bg-silver-lt text-ink-mid'}`}>{isRecorded ? 'Recorded' : row?.census !== null && row?.census !== undefined ? 'Forecast' : 'None'}</span>
              <input type="number" inputMode="numeric" min={0} max={unit.beds ?? 2000} aria-label={`Today's census on ${unit.name}`} className={INPUT} placeholder="new" value={draft[unit.id] ?? ''} onChange={(event) => setDraft((current) => ({ ...current, [unit.id]: event.target.value }))} />
            </label>
          )
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button icon={ClipboardCheck} size="sm" disabled={changed.length === 0 || record.isPending} onClick={() => record.mutate(changed, { onSuccess: () => setDraft({}) })}>
          {record.isPending ? 'Recording…' : changed.length > 1 ? `Record ${changed.length} units` : 'Record'}
        </Button>
        <span className="text-[11px] text-ink-mid">The engine answers each count at once; anything it leaves to you appears under Needs you.</span>
      </div>
      <FormError error={record.error} fallback="The census could not be recorded. Try again." />
    </div>
  )
}
